/**
 * Shared date/time input utilities
 *
 * Used by AddTrainingModal, AddClassModal, and AddPromotionModal to avoid
 * duplicating masking, conversion, and formatting logic.
 */

// ── Time options (30-min intervals, used in <datalist>) ─────────────────────

export const TIME_OPTIONS = [
  '12:00 AM', '12:30 AM',
  '1:00 AM',  '1:30 AM',
  '2:00 AM',  '2:30 AM',
  '3:00 AM',  '3:30 AM',
  '4:00 AM',  '4:30 AM',
  '5:00 AM',  '5:30 AM',
  '6:00 AM',  '6:30 AM',
  '7:00 AM',  '7:30 AM',
  '8:00 AM',  '8:30 AM',
  '9:00 AM',  '9:30 AM',
  '10:00 AM', '10:30 AM',
  '11:00 AM', '11:30 AM',
  '12:00 PM', '12:30 PM',
  '1:00 PM',  '1:30 PM',
  '2:00 PM',  '2:30 PM',
  '3:00 PM',  '3:30 PM',
  '4:00 PM',  '4:30 PM',
  '5:00 PM',  '5:30 PM',
  '6:00 PM',  '6:30 PM',
  '7:00 PM',  '7:30 PM',
  '8:00 PM',  '8:30 PM',
  '9:00 PM',  '9:30 PM',
  '10:00 PM', '10:30 PM',
  '11:00 PM', '11:30 PM',
];

// ── Date masking ─────────────────────────────────────────────────────────────

/**
 * Applies dd/MM/yyyy mask as the user types.
 * Strips non-digits and inserts slashes automatically.
 */
export const applyDateMask = (value: string): string => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 2) return numbers;
  if (numbers.length <= 4) return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
  return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
};

/**
 * Converts display format dd/MM/yyyy → internal yyyy-MM-dd.
 * Returns '' if the display string is incomplete or invalid.
 */
export const convertDisplayToInternal = (displayDate: string): string => {
  if (!displayDate || displayDate.length !== 10) return '';
  const [day, month, year] = displayDate.split('/');
  if (day?.length === 2 && month?.length === 2 && year?.length === 4) {
    return `${year}-${month}-${day}`;
  }
  return '';
};

/**
 * Converts internal yyyy-MM-dd → display dd/MM/yyyy.
 * Returns '' if the internal string is invalid.
 */
export const convertInternalToDisplay = (internalDate: string): string => {
  if (!internalDate || internalDate.length !== 10) return '';
  const [year, month, day] = internalDate.split('-');
  if (year?.length === 4 && month?.length === 2 && day?.length === 2) {
    return `${day}/${month}/${year}`;
  }
  return '';
};

// ── Time formatting ──────────────────────────────────────────────────────────

/**
 * Converts a partial or complete time string to "H:MM AM/PM" format.
 * Returns null if the input cannot be parsed.
 *
 * Accepts:
 *   "14:30"        → "2:30 PM"
 *   "2:30 PM"      → "2:30 PM" (passthrough)
 *   "2"            → "2:00 AM" (smart guess for morning)
 *   "14"           → "2:00 PM"
 */
export const formatTimeToAMPM = (timeInput: string): string | null => {
  if (!timeInput) return null;
  const trimmed = timeInput.trim();

  // Already in AM/PM format
  if (/AM|PM/i.test(trimmed)) {
    // Normalise spacing
    const m = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (m) {
      return `${parseInt(m[1], 10)}:${m[2]} ${m[3].toUpperCase()}`;
    }
    return trimmed;
  }

  // 24-hour "HH:MM"
  const full = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (full) {
    const h = parseInt(full[1], 10);
    const min = full[2];
    if (h >= 0 && h <= 23 && parseInt(min, 10) >= 0 && parseInt(min, 10) <= 59) {
      const period = h >= 12 ? 'PM' : 'AM';
      const display = h === 0 ? 12 : h > 12 ? h - 12 : h;
      return `${display}:${min} ${period}`;
    }
  }

  // Single or double digit hour only, e.g. "9" or "14"
  const hourOnly = trimmed.match(/^(\d{1,2})$/);
  if (hourOnly) {
    const h = parseInt(hourOnly[1], 10);
    if (h >= 0 && h <= 23) {
      const period = h >= 12 ? 'PM' : 'AM';
      const display = h === 0 ? 12 : h > 12 ? h - 12 : h;
      return `${display}:00 ${period}`;
    }
  }

  return null;
};

/**
 * Converts "H:MM AM/PM" or "HH:MM" to 24-hour "HH:MM".
 * Returns '' if the input cannot be parsed.
 */
export const convertTimeTo24Hour = (timeInput: string): string => {
  if (!timeInput) return '';
  const time = timeInput.trim();

  // Already 24-hour "HH:MM" (no AM/PM)
  const h24 = time.match(/^(\d{1,2}):(\d{2})$/);
  if (h24) {
    const h = parseInt(h24[1], 10);
    if (h >= 0 && h <= 23) {
      return `${h.toString().padStart(2, '0')}:${h24[2]}`;
    }
  }

  // AM/PM format
  const ampm = time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const min = ampm[2];
    const period = ampm[3].toUpperCase();
    if (period === 'PM' && h !== 12) h += 12;
    else if (period === 'AM' && h === 12) h = 0;
    return `${h.toString().padStart(2, '0')}:${min}`;
  }

  return '';
};
